import { useState } from "react";
import { CheckCircle2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import BulkDropzone from "@/components/BulkDropzone";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LANDING_ADMIN } from "@/constants/testIds";

/** Carga masiva de testimonios: cada captura de pantalla subida crea un
 * testimonio nuevo con un nombre provisorio, editable en esta misma vista
 * previa (o después, desde la lista) antes/luego de confirmar. */
export default function BulkTestimonialsUploadModal({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState("drop"); // drop | review | uploading | result
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);

  const reset = () => {
    rows.forEach((r) => URL.revokeObjectURL(r.previewUrl));
    setStep("drop"); setRows([]); setProgress({ done: 0, total: 0 }); setResult(null);
  };

  const handleClose = (isOpen) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  const handleFiles = (files) => {
    const built = files.map((file, idx) => ({
      key: `${idx}-${file.name}`,
      file,
      previewUrl: URL.createObjectURL(file),
      filename: file.name,
      patientName: `Paciente ${idx + 1}`,
    }));
    setRows(built);
    setStep("review");
  };

  const updateName = (key, patientName) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, patientName } : r)));
  const removeRow = (key) => setRows((rs) => rs.filter((r) => r.key !== key));

  const handleConfirm = async () => {
    setStep("uploading");
    let success = 0;
    const errors = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      setProgress({ done: i, total: rows.length });
      try {
        const formData = new FormData();
        formData.append("file", row.file);
        const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
        await api.post("/landing/testimonials", {
          patient_name: row.patientName.trim() || `Paciente ${i + 1}`,
          image_url: data.url,
        });
        success++;
      } catch (err) {
        errors.push({ filename: row.filename, error: formatApiErrorDetail(err.response?.data?.detail) });
      }
    }
    setProgress({ done: rows.length, total: rows.length });
    setResult({ success, errors });
    setStep("result");
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid={LANDING_ADMIN.testimonialBulkModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Subir testimonios en lote</DialogTitle>
        </DialogHeader>

        {step === "drop" && (
          <div className="py-2">
            <BulkDropzone
              onFiles={handleFiles}
              accept="image/png,image/jpeg,image/jpg,image/webp,image/avif"
              label="Arrastrá o hacé click para elegir varias capturas de reseñas"
              hint="Cada imagen crea un testimonio nuevo. Podés escribir el nombre de la paciente ahora o editarlo después desde la lista."
              testId={LANDING_ADMIN.testimonialBulkDropzone}
            />
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">{rows.length} reseña(s)</p>
            <div className="border border-border rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
              <Table data-testid={LANDING_ADMIN.testimonialBulkTable}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16"></TableHead>
                    <TableHead>Archivo</TableHead>
                    <TableHead>Nombre de la paciente</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, idx) => (
                    <TableRow key={r.key} data-testid={`${LANDING_ADMIN.testimonialBulkRowPrefix}${idx}`}>
                      <TableCell>
                        <img src={r.previewUrl} alt={r.filename} className="w-12 h-12 object-cover rounded border border-border" />
                      </TableCell>
                      <TableCell>
                        <p className="text-sm truncate max-w-[160px]">{r.filename}</p>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={r.patientName}
                          onChange={(e) => updateName(r.key, e.target.value)}
                          className="h-8 text-sm"
                          data-testid={`${LANDING_ADMIN.testimonialBulkNameInputPrefix}${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => removeRow(r.key)} data-testid={`${LANDING_ADMIN.testimonialBulkRemoveButtonPrefix}${idx}`}>
                          <X className="w-3.5 h-3.5 text-muted-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {step === "uploading" && (
          <div className="py-10 space-y-3" data-testid={LANDING_ADMIN.testimonialBulkProgress}>
            <p className="text-sm text-center text-muted-foreground">Subiendo {progress.done} de {progress.total}...</p>
            <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} />
          </div>
        )}

        {step === "result" && result && (
          <div className="py-6 text-center space-y-3" data-testid={LANDING_ADMIN.testimonialBulkResultSummary}>
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <p className="text-lg font-serif text-foreground">Carga completa</p>
            <p className="text-sm text-muted-foreground">{result.success} testimonio(s) creados &middot; {result.errors.length} error(es)</p>
            {result.errors.length > 0 && (
              <ul className="text-xs text-destructive text-left max-w-sm mx-auto space-y-1">
                {result.errors.map((e, i) => <li key={i}>{e.filename}: {e.error}</li>)}
              </ul>
            )}
          </div>
        )}

        <DialogFooter>
          {step === "drop" && (
            <Button variant="outline" onClick={() => handleClose(false)} data-testid={LANDING_ADMIN.testimonialBulkCancelButton}>Cancelar</Button>
          )}
          {step === "review" && (
            <Button onClick={handleConfirm} disabled={rows.length === 0} data-testid={LANDING_ADMIN.testimonialBulkConfirmButton}>Confirmar carga ({rows.length})</Button>
          )}
          {step === "result" && <Button onClick={() => handleClose(false)}>Cerrar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
